import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClient } from '../../../services/http/api-client';
import { FINANCE_TABS } from '../finance-tabs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../modal/confirmdialog/confirmdialog.component';

interface AccountControl { bankImportId?: string; account: string; opening: string; credits: string; debits: string; closing: string; source: string; basis: string; }
interface ReconcilingItem { id: string; account: string; originDate: string; amount: string; owner: string; purpose: string; evidence: string; resolutions: { date: string; amount: string; evidence: string }[]; }
interface Worksheet { month: string; accounts: AccountControl[]; items: ReconcilingItem[]; notes: string; }
interface MonthlyDetail extends MonthlyResult { userCash?: { candidateClosingBalance: string; processedWithdrawalsInitiated: string; status: string; basis: string; retrievedAt: string; unknownBalanceAccounts: number; balanceEvidenceExceptions: number; noWalletActivityAccounts: number; estimatedAccountsWithCaveats: number; customers: object[]; withdrawals: object[]; limitations: string[] }; statement: { opening: string; credits: string; debits: string; closing: string } | null; transactions: { id: string; posted: string; amount: string; source: string; destination: string; providerInvoiceEvidence?: { invoiceNumber: string; localPdf?: string }; sourceTitle?: string; sourceSubtitle?: string; destinationTitle?: string; destinationSubtitle?: string; attributionStatus: string; platformLabel?: string; userLabel?: string; teamLabel?: string; purpose?: string; needsReview?: boolean; attributionReasons?: string[]; runningBalance?: string | null }[]; stripe: object | null; items: object[]; banks: object[]; controls: object[]; changes: object | null; limitations: string[]; }
interface MonthlyResult { month: string; status: string; masterGlDifference: string | null; issues: string[]; }
interface StatementFile { id: string; month: string; kind: string; originalName: string; uploadedAt: string; uploadedBy: string; sha256: string; }
interface ReportRun { id: string; state: string; stage: string; createdAt: string; result?: { status: string; apiRows: number; postingRows: number; exceptions: number; monthly?: MonthlyResult; }; }
interface Overview { account: { id: string; name: string }; environment: string; statements: StatementFile[]; runs: ReportRun[]; }
interface StatementMonth { month: string; pdf: boolean; csv: boolean; overdue: boolean; status: string; }

@Component({
  selector: 'app-dwolla-reconciliation', standalone: false,
  templateUrl: './dwolla-reconciliation.component.html', styleUrls: ['./dwolla-reconciliation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class DwollaReconciliationComponent implements OnInit, OnDestroy {
  tabs = FINANCE_TABS;
  month = new Date(new Date().getFullYear(), new Date().getMonth(), 0, 12).toISOString().slice(0, 7);
  currentMonth = new Date().toISOString().slice(0, 7);
  worksheet: Worksheet | null = null;
  worksheetRevision = 0;
  monthlyDetail: MonthlyDetail | null = null;
  transactionSearch = '';
  cashDirection = 'all';
  get filteredTransactions() {
    return (this.monthlyDetail?.transactions || []).filter(row => (this.cashDirection === 'all' || (this.cashDirection === 'credit' ? Number(row.amount) > 0 : Number(row.amount) < 0)) && JSON.stringify(row).toLowerCase().includes(this.transactionSearch.toLowerCase()));
  }
  async viewMonthly(run: ReportRun): Promise<void> {
    if (this.busy) { return; }
    this.busy = true; this.error = ''; this.monthlyDetail = null;
    try {
      this.monthlyDetail = await firstValueFrom(this.resource().one('runs', run.id).one('monthly').get()) as MonthlyDetail;
      this.month = this.monthlyDetail.month; this.worksheet = null;
      this.transactionSearch = ''; this.cashDirection = 'all';
    } catch (error) { this.showError(error); } finally { this.busy = false; }
  }
  carriedFrom: string | null = null;
  accountOptions = [{ id: 'master', label: 'Dwolla master GL' }, { id: 'bank-operating', label: 'Bank operating' }, { id: 'bank-customer-funds', label: 'Bank customer funds' }, { id: 'stripe-clearing', label: 'Stripe clearing' }];
  async loadMonth(): Promise<void> {
    if (this.busy || !this.data) { return; }
    this.busy = true; this.error = ''; this.message = ''; this.monthlyDetail = null;
    this.transactionSearch = ''; this.cashDirection = 'all';
    try {
      const result = await firstValueFrom(this.resource().one('months', this.month).get()) as { worksheet: Worksheet; revision: number; carriedFrom: string | null };
      this.worksheet = result.worksheet; this.worksheetRevision = result.revision; this.carriedFrom = result.carriedFrom;
      const run = this.data.runs.filter(r => r.state === 'complete' && r.result?.monthly?.month === this.month)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
      if (run) {
        const detail = await firstValueFrom(this.resource().one('runs', run.id).one('monthly').get()) as MonthlyDetail;
        if (detail.month !== this.month) { throw new Error('Returned report does not match the selected month'); }
        this.monthlyDetail = detail;
      } else {
        this.message = 'No completed monthly report is available for ' + this.month + '.';
      }
    } catch (error) { this.showError(error); } finally { this.busy = false; }
  }
  async moveMonth(delta: number): Promise<void> {
    if (this.busy) { return; }
    const next = new Date(this.month + '-01T00:00:00Z'); next.setUTCMonth(next.getUTCMonth() + delta);
    const month = next.toISOString().slice(0, 7);
    if (month < '2025-12' || month > this.currentMonth) { return; }
    this.month = month; this.worksheet = null; await this.loadMonth();
  }
  addAccount(): void {
    const account = this.accountOptions.find(a => !this.worksheet?.accounts.some(c => c.account === a.id));
    if (this.worksheet && account) { this.worksheet.accounts.push({ account: account.id, opening: '', credits: '', debits: '', closing: '', source: '', basis: '' }); }
  }
  addItem(): void {
    this.worksheet?.items.push({ id: crypto.randomUUID(), account: 'master', originDate: this.month + '-01', amount: '', owner: '', purpose: '', evidence: '', resolutions: [] });
  }
  async saveMonth(generate = false): Promise<void> {
    if (this.busy || !this.worksheet || this.worksheet.month !== this.month) { return; }
    this.busy = true; this.error = ''; this.message = '';
    try {
      const saved = await firstValueFrom(this.resource().one('months').customPOST({ revision: this.worksheetRevision, worksheet: this.worksheet })) as { revision: number; worksheet: Worksheet };
      this.worksheetRevision = saved.revision; this.worksheet = saved.worksheet; this.carriedFrom = null;
      if (generate) {
        await firstValueFrom(this.resource().one('runs').customPOST({ month: this.month, worksheetRevision: this.worksheetRevision }));
        this.message = 'Monthly report queued. Its worksheet revision is frozen. Extract the ZIP and open monthly-reconciliation.html.';
        await this.refresh();
      } else { this.message = 'Worksheet revision ' + this.worksheetRevision + ' saved. Earlier revisions and reports are retained.'; }
    } catch (error) { this.showError(error); } finally { this.busy = false; }
  }
  data: Overview | null = null;
  allDates = true;
  startDate = '';
  endDate = '';
  timezone = 'UTC';
  files: File[] = [];
  confirmed = false;
  busy = false;
  error = '';
  message = '';
  statementMonths: StatementMonth[] = [];
  completedMonth = '';
  get monthsNeedingUpload(): number { return this.statementMonths.filter(month => month.overdue).length; }
  private destroyed = false;
  private poll?: ReturnType<typeof setTimeout>;
  constructor(private api: ApiClient, private dialog: MatDialog) {}
  private resource() { return this.api.one('admin').one('ledger').one('dwolla-reconciliation'); }
  ngOnInit(): void { void this.refresh(); }
  ngOnDestroy(): void { this.destroyed = true; clearTimeout(this.poll); }
  async refresh(): Promise<void> {
    clearTimeout(this.poll);
    try {
      const data = await firstValueFrom(this.resource().get()) as Overview;
      if (this.destroyed) { return; }
      this.data = data;
      this.updateStatementMonths();
      if (data.runs.some(r => ['queued', 'running'].includes(r.state))) {
        this.poll = setTimeout(() => void this.refresh(), 5000);
      }
    } catch (error) { this.showError(error); }
  }
  updateStatementMonths(now = new Date()): void {
    const current = now.toISOString().slice(0, 7);
    this.completedMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)).toISOString().slice(0, 7);
    const uploaded = new Map<string, Set<string>>();
    for (const file of this.data?.statements || []) {
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(file.month) || file.month > current) { continue; }
      if (!uploaded.has(file.month)) { uploaded.set(file.month, new Set()); }
      uploaded.get(file.month)!.add(file.kind);
    }
    const months = [...uploaded.keys(), this.completedMonth].sort();
    const cursor = new Date(months[0] + '-01T00:00:00Z');
    const last = months[months.length - 1];
    this.statementMonths = [];
    while (cursor.toISOString().slice(0, 7) <= last) {
      const month = cursor.toISOString().slice(0, 7);
      const files = uploaded.get(month);
      const pdf = files?.has('pdf') || false, csv = files?.has('csv') || false;
      const overdue = month <= this.completedMonth && (!pdf || !csv);
      const status = pdf && csv ? 'Both files uploaded' : !overdue ? 'Month in progress' :
        !pdf && !csv ? 'Upload needed: PDF and CSV' : !pdf ? 'Upload needed: PDF' : 'Upload needed: CSV';
      this.statementMonths.push({ month, pdf, csv, overdue, status });
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
    this.statementMonths.reverse();
  }
  chooseFiles(event: Event): void {
    this.files = Array.from((event.target as HTMLInputElement).files || []);
    this.confirmed = false;
    this.error = '';
    if (this.files.length > 160 || this.files.some(f => f.size > 2 * 1024 * 1024) || this.files.reduce((n, f) => n + f.size, 0) > 50 * 1024 * 1024) {
      this.error = 'Select up to 160 files, at most 2 MB each and 50 MB total.';
      this.files = [];
    }
  }
  async upload(): Promise<void> {
    if (!this.data || !this.confirmed || !this.files.length || this.busy) { return; }
    this.busy = true; this.error = ''; this.message = '';
    try {
      const form = new FormData();
      form.append('accountId', this.data.account.id);
      this.files.forEach(f => form.append('files', f, f.name));
      await firstValueFrom(this.resource().one('statements').customPOST(form, undefined, undefined, { 'Content-Type': undefined }));
      this.message = 'Original statements archived. Generate a new report to include them.';
      this.files = []; this.confirmed = false;
      await this.refresh();
    } catch (error) { this.showError(error); } finally { this.busy = false; }
  }
  async generate(): Promise<void> {
    if (this.busy || !this.data) { return; }
    if (!this.allDates && (!this.startDate || !this.endDate || this.startDate > this.endDate)) { this.error = 'Choose a valid start and end date.'; return; }
    this.busy = true; this.error = ''; this.message = '';
    try {
      await firstValueFrom(this.resource().one('runs').customPOST({ timezone: this.timezone,
        ...(this.allDates ? {} : { startDate: this.startDate, endDate: this.endDate }) }));
      this.message = 'Report queued. You can leave this page and return to download it.';
      await this.refresh();
    } catch (error) { this.showError(error); } finally { this.busy = false; }
  }
  async download(run: ReportRun): Promise<void> {
    this.busy = true; this.error = '';
    try {
      const blob = await firstValueFrom(this.resource().one('runs', run.id).one('download').withHttpConfig({ responseType: 'blob' }).get()) as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = run.result?.monthly ? 'wallit-reconciliation-' + run.result.monthly.month + '-' + run.id + '.zip' : 'where-the-master-dwolla-balance-came-from.zip'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) { this.showError(error); } finally { this.busy = false; }
  }
  async remove(run: ReportRun): Promise<void> {
    if (this.busy || !['complete', 'failed'].includes(run.state)) { return; }
    this.busy = true; this.error = ''; this.message = '';
    try {
      const confirmed = await firstValueFrom(this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
        data: new ConfirmDialogModel('Remove generated report',
          'Remove this report from the list? Its audit record and stored evidence will be retained. Uploaded monthly statements will not be affected.',
          false, 'Remove from list', 'Cancel'),
        autoFocus: false,
      }).afterClosed());
      if (!confirmed) { return; }
      await firstValueFrom(this.resource().one('runs', run.id).remove());
      this.message = 'Report removed from the list. Audit evidence has been retained.';
      await this.refresh();
    } catch (error) { this.showError(error); } finally { this.busy = false; }
  }
  private showError(error: unknown): void {
    const response = error as { error?: { error?: string }; message?: string };
    this.error = response.error?.error || response.message || 'The request failed. Please try again.';
  }
}
